import { CommonModule } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { UserService } from '@services/user.service'
import { AuthenticationService } from '@services/authentication.service'
import { combineLatest, firstValueFrom, Subscription } from 'rxjs'
import { filter, take } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

type SignInChoice = { id: string; name: string; label: string; method: string }
type AccessOptions = {
  organizationId: string; requireCompanySso: boolean
  connections: SignInChoice[]; recoveryConnections: SignInChoice[]
}

@Component({
  selector: 'organization-access', standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './organization-access.component.html',
  styleUrls: ['./organization-access.component.css'],
})
export class OrganizationAccessComponent implements OnInit, OnDestroy {
  authenticated = false
  loading = true
  saving = false
  error = ''
  message = ''
  verificationCompleted = false
  recoverySucceeded = false
  code = ''
  options?: AccessOptions
  private subscription?: Subscription
  private get organizationId() {
    const id = this.route.snapshot.queryParamMap.get('organizationId')
    return id && /^org_[A-Za-z0-9]+$/.test(id) ? id : undefined
  }
  constructor(private auth: AuthenticationService, private http: HttpClient,
    private route: ActivatedRoute, private router: Router, private changes: ChangeDetectorRef, private users: UserService) {}

  ngOnInit() {
    this.subscription = combineLatest([this.auth.isLoading$, this.auth.isAuthenticated$]).pipe(
      filter(([loading]) => !loading), take(1),
    ).subscribe(([, authenticated]) => {
      this.authenticated = authenticated
      if (authenticated) void this.load()
      else this.loading = false
    })
  }
  ngOnDestroy() { this.subscription?.unsubscribe(); this.code = '' }

  async load() {
    this.loading = true
    this.error = ''
    try {
      if (await this.auth.completeOrganizationSignInVerification()) {
        this.verificationCompleted = true
        this.message = 'Sign-in verified. Return to organization settings, or choose an allowed sign-in method if this method is blocked.'
      }
    } catch (error: any) {
      this.error = error?.message || 'Sign-in verification could not be completed. Retry from organization settings.'
    }
    try {
      this.options = await firstValueFrom(this.http.get<AccessOptions>(`${environment.api}/org/sso/access`, {
        params: this.organizationId ? { organizationId: this.organizationId } : {},
      }))
    } catch {
      this.error = 'Sign-in choices could not be confirmed. Sign in with an existing organization account, or retry.'
    } finally { this.loading = false; this.changes.markForCheck() }
  }

  private returnTarget() {
    return '/organization-access' + (this.organizationId ? `?organizationId=${encodeURIComponent(this.organizationId)}` : '')
  }
  async returnToSettings() {
    if (!this.verificationCompleted || !this.authenticated || this.loading || this.saving) return
    if (this.recoverySucceeded) await this.refreshRecoveredProfile()
    else await this.router.navigate(['/organization'], { queryParams: { tab: 'basic-info' } })
  }
  signIn() {
    this.code = ''
    this.auth.loginWithRedirect({ appState: { target: this.returnTarget() }, authorizationParams: { prompt: 'login', max_age: 0 } })
  }
  continueWith(choice: SignInChoice, recovery = false) {
    const choices = recovery ? this.options?.recoveryConnections : this.options?.connections
    if (!this.options || !choices?.some(c => c.id === choice.id && c.name === choice.name)) return
    this.code = ''
    this.auth.loginWithRedirect({ appState: { target: recovery ? this.returnTarget() : this.message ? '/organization?tab=basic-info' : '/projects' },
      authorizationParams: { organization: this.options.organizationId, connection: choice.name, prompt: 'login', max_age: 0 } })
  }
  async recover() {
    if (this.recoverySucceeded) { this.code = ''; await this.returnToSettings(); return }
    if (!this.code.trim() || this.saving || !this.options) return
    const code = this.code.trim()
    this.code = ''
    this.saving = true
    this.error = ''
    try {
      await firstValueFrom(this.auth.recoverOrganizationSignIn(code, this.options.organizationId))
      this.recoverySucceeded = true
      this.verificationCompleted = true
      await this.refreshRecoveredProfile()
    } catch (error: any) {
      this.error = error?.status === 428
        ? 'Authenticate again using a recovery sign-in option below, then enter your recovery code again.'
        : 'Recovery was not completed. Use the unused code saved by this administrator and an account already connected to it.'
    } finally { this.saving = false; this.changes.markForCheck() }
  }
  private async refreshRecoveredProfile() {
    this.saving = true
    this.error = ''
    this.message = 'Recovery succeeded. Loading your account profile...'
    try {
      const user = await firstValueFrom(this.users.getCurrentUser())
      if (!user) throw new Error('Profile is unavailable')
      this.message = 'Recovery succeeded. Your account profile is restored.'
      await this.router.navigate(['/organization'], { queryParams: { tab: 'basic-info' } })
    } catch {
      this.error = 'Recovery succeeded, but your profile or organization settings could not be loaded. Select Return to organization settings to retry. Your recovery code has already been used.'
    } finally { this.saving = false; this.changes.markForCheck() }
  }

  label(choice: SignInChoice) {
    return ({ password: 'email and password', google: 'Google', microsoft: 'Microsoft' } as Record<string, string>)[choice.method] || choice.label
  }
}
